// Cockpit revamp — header status actions.
//
// Renders every transition button in the matrix; disables the ones not
// allowed from the current status (with a tooltip explaining why). On
// click, opens a ConfirmReasonModal that captures a required reason and
// posts to /campaigns/:id/transitions/:target. The force-end modal also
// surfaces the LT-348 §2.4 #4 also_cancel_callbacks toggle.
import React, { useState } from 'react';
import {
  OctagonAlert,
  Pause,
  Play,
  Rocket,
  Square,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '../../../ui';
import {
  campaignsAPI,
  extractCampaignApiError,
} from '../../../services/campaignsAPI';
import type { Campaign } from '../../../types/campaign';
import {
  TRANSITIONS,
  TRANSITION_TARGET,
  isTransitionAllowed,
  type TransitionDef,
  type TransitionKind,
} from './transitions';
import { TransitionConfirmModal } from './TransitionConfirmModal';

const ICON_FOR: Record<TransitionDef['iconName'], LucideIcon> = {
  Rocket,
  Pause,
  Play,
  OctagonAlert,
  Square,
};

interface CampaignStatusActionsProps {
  campaign: Campaign;
  onCampaignChange: (next: Campaign) => void;
  onAuditChanged: () => void;
}

const CampaignStatusActions: React.FC<CampaignStatusActionsProps> = ({
  campaign,
  onCampaignChange,
  onAuditChanged,
}) => {
  const [pending, setPending] = useState<TransitionDef | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (
    def: TransitionDef,
    reason: string,
    alsoCancelCallbacks: boolean,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const next = await campaignsAPI.transition(
        campaign.id,
        TRANSITION_TARGET[def.kind],
        {
          reason: reason || undefined,
          also_cancel_callbacks:
            def.kind === 'force_end' ? alsoCancelCallbacks : undefined,
        },
      );
      onCampaignChange(next);
      onAuditChanged();
      setPending(null);
    } catch (e) {
      setError(extractCampaignApiError(e, 'Transition failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {TRANSITIONS.map((def) => {
        const allowed = isTransitionAllowed(campaign.status, def.kind);
        const Icon = ICON_FOR[def.iconName];
        return (
          <Button
            key={def.kind}
            intent={def.intent}
            disabled={!allowed}
            onClick={() => allowed && setPending(def)}
            title={
              allowed
                ? def.note
                : `Not allowed from status: ${campaign.status}`
            }
            aria-label={
              allowed
                ? `${def.label} — ${def.note}`
                : `${def.label} — disabled in status ${campaign.status}`
            }
          >
            <Icon size={14} strokeWidth={2.5} />
            {def.label}
          </Button>
        );
      })}

      <TransitionConfirmModal
        open={pending !== null}
        // When `pending` is null we still render the modal closed; pass a
        // safe default for the definition so prop types remain happy.
        definition={pending ?? TRANSITIONS[0]}
        campaign={campaign}
        saving={busy}
        error={error}
        onCancel={() => {
          if (!busy) {
            setPending(null);
            setError(null);
          }
        }}
        onConfirm={(reason, alsoCancel) => {
          if (pending) submit(pending, reason, alsoCancel);
        }}
      />
    </div>
  );
};

export default CampaignStatusActions;
